/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable class-methods-use-this */
import {
  ADD_CONCERN,
  CHANGE_CONCERN,
  ConcernActions,
  ConnectTenantConcern,
  REMOVE_CONCERN,
  RESET_CONCERNS,
  TenantConcern,
} from '@models';
import * as Services from '@services';
import { EntityDto } from '@services';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class ConcernStore {
  dispachedConcerns: Services.Concern[] = [Services.InitialConcern as Services.Concern];
  tenantConcernCategories: Services.TenantConcernCategory[] = [];
  concernCategories: Map<string, Services.ConcernCategory> = new Map();
  concernCategory!: Services.ConcernCategory | undefined;

  tenantConcerns: Map<string, TenantConcern> = new Map();
  tenantConcern: TenantConcern | undefined;

  skipCount: number = 0;
  currentPage: number = 1;
  totalCount: number = 0;
  loading: boolean = false;
  maxResultCount: number = 15;

  constructor() {
    makeAutoObservable(this);
  }

  async getAllCategories(request: Services.PagedConcernCategoryRequestDto) {
    this.setLoading(true);
    try {
      const result = await Services.ConcernService.getAllCategories(request);
      runInAction(() => {
        result.items.forEach((x) => {
          this.concernCategories.set(x.id, x);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getAllTenantConcerns(request: Services.PagedTenantConcernsRequestDto) {
    this.setLoading(true);
    try {
      const result = await Services.ConcernService.getAllTenantConcerns(request);
      runInAction(() => {
        result.items.forEach((x) => {
          this.tenantConcerns.set(x.id, x);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getTenantConcern(entity: EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await Services.ConcernService.getTenantConcern(entity);
      runInAction(() => {
        this.tenantConcern = result;
      });
      this.setLoading(false);
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  }

  async connectTenantConcern(request: ConnectTenantConcern) {
    this.setLoading(true);
    try {
      const result = await Services.ConcernService.connectTenantConcern(request);
      runInAction(() => {
        this.tenantConcerns.set(result.id, result);
      });
      this.setLoading(false);
      message.success({
        content: 'New setup service for online booking was successfully created!',
      });
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  }

  async getConcernCategory(entity: EntityDto<string>) {
    const result = await Services.ConcernService.getConcernCategory(entity);
    runInAction(() => {
      this.concernCategory = result;
      this.dispachedConcerns = [
        ...(result.concerns || (Services.InitialConcern as Services.Concern)),
      ];
    });
  }

  async updateConcernCategory(concernCategory: Services.CreateConcernCategory) {
    try {
      const result = await Services.ConcernService.update(concernCategory);
      runInAction(() => {
        this.concernCategory = result;
      });
      message.success({ content: 'Successfully updated!' });
    } catch (error) {}
  }

  async deleteConcern(entity: EntityDto<string>) {
    try {
      const result = await Services.ConcernService.deleteConcern(entity);
      runInAction(() => {
        this.concernCategory = result;
        this.dispachedConcerns = this.dispachedConcerns.filter((x) => x.id !== entity.id);
      });
      message.success({ content: 'Successfully deleted!' });
    } catch (error) {
      console.error(error);
    }
  }

  async updateOrCreateConcern(concernIndex: number) {
    try {
      const concern = this.dispachedConcerns[concernIndex];
      if (concern.id) {
        const result = await Services.ConcernService.updateConcern(concern);
        runInAction(() => {
          this.concernCategory!.concerns = this.concernCategory!.concerns.map((x) => {
            if (x.id === concern.id) {
              return { ...x, ...result };
            }
            return x;
          });
        });
      } else {
        const result = await Services.ConcernService.createConcern({
          ...concern,
          categoryId: this.concernCategory!.id,
        });
        runInAction(() => {
          this.concernCategory!.concerns = [result].concat(this.concernCategory!.concerns);
        });
      }
      this.dispatchConcerns({
        type: CHANGE_CONCERN,
        concern: { ...concern, inEdit: false },
        index: concernIndex,
      });
      message.success({ content: 'Successfully updated!' });
    } catch (error) {
      console.error(error);
    }
  }

  async getAllByTenantId(entityDto: Services.EntityDto) {
    const result = await Services.ConcernService.getAllByTenantId(entityDto);
    runInAction(() => {
      this.tenantConcernCategories = result;
    });
  }

  async create(concernCategory: Services.CreateConcernCategory) {
    try {
      const result = await Services.ConcernService.create(concernCategory);
      runInAction(() => {
        this.concernCategories.set(result.id, result);
        this.totalCount += 1;
      });
      message.success({
        content: 'Successfully created!',
      });
    } catch (error) {
      console.error(error);
    }
  }

  async updateTenantConcern(request: Services.UpdateTenantConcern) {
    const result = await Services.ConcernService.updateTenantConcern(request);
    runInAction(() => {
      this.tenantConcernCategories = this.tenantConcernCategories.map((x) => {
        x.concerns = x.concerns.map((concern) => {
          if (concern.tenantConcernId === request.id) {
            return { ...concern, ...result };
          }
          return concern;
        });
        return x;
      });
    });
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  handlePageChange = (pagination: any) => {
    runInAction(() => {
      this.maxResultCount = pagination.pageSize;
      this.skipCount = (pagination.current - 1) * pagination.pageSize;
      this.currentPage = pagination.current;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };

  clearTenantConcerns = () => {
    runInAction(() => {
      this.tenantConcerns.clear();
      this.totalCount = 0;
    });
  };

  resetTenantConcerns = () => {
    runInAction(() => {
      this.tenantConcerns.clear();
      this.totalCount = 0;
      this.skipCount = 0;
      this.currentPage = 1;
    });
  };

  clearTenantConcern = () => {
    runInAction(() => {
      this.tenantConcern = undefined;
    });
  };

  dispatchConcerns = (action: ConcernActions) => {
    switch (action.type) {
      case ADD_CONCERN:
        runInAction(() => {
          this.dispachedConcerns = [
            ...this.dispachedConcerns,
            Services.InitialConcern as Services.Concern,
          ];
        });
        return;
      case REMOVE_CONCERN:
        runInAction(() => {
          this.dispachedConcerns = [
            ...this.dispachedConcerns.filter((_concern: any, i: number) => i !== action.index),
          ];
        });
        return;
      case CHANGE_CONCERN:
        const items = [...this.dispachedConcerns];
        items[action.index] = { ...items[action.index], ...action.concern };
        runInAction(() => {
          this.dispachedConcerns = items;
        });
        return;
      case RESET_CONCERNS:
        runInAction(() => {
          this.dispachedConcerns = [Services.InitialConcern as Services.Concern];
        });
        return;
      default:
        return this.dispachedConcerns;
    }
  };

  clearConcernCategory = () => {
    runInAction(() => {
      this.concernCategory = undefined;
      this.dispachedConcerns = [Services.InitialConcern as Services.Concern];
    });
  };
}

export default ConcernStore;
